/**
 * Rebuy SmartCart — Gift Your Purchase (gift note + gift wrapping)
 * Injects unified section above cart-subtotal; gift note uses modal, gift wrap adds to cart directly.
 */
(function () {
  const CONFIG_EL_ID = 'DnGiftPurchaseConfig';
  const MODAL_ID = 'DnGiftNoteModal';
  const SECTION_ATTR = 'data-dn-gift-purchase';
  const COLLAPSIBLE_ATTR = 'data-dn-gift-purchase-collapsible';
  const NOTE_TRIGGER_ATTR = 'data-dn-gift-note-trigger';
  const WRAP_TRIGGER_ATTR = 'data-dn-gift-wrap-trigger';
  const NOTE_ROW_ATTR = 'data-dn-gift-note-row';
  const WRAP_ROW_ATTR = 'data-dn-gift-wrap-row';
  const NOTE_LINE_EDIT_ATTR = 'data-dn-gift-note-line-edit';
  const NOTE_LINE_ACTIONS_ATTR = 'data-dn-gift-note-line-actions';
  const EDIT_NOTE_LABEL = 'Edit Note';
  const MESSAGE_DISPLAY_LIMIT = 60;
  const MESSAGE_TRUNCATE_ATTR = 'data-dn-gift-note-message-truncate';
  const MESSAGE_FULL_ATTR = 'data-dn-message-full';
  const MESSAGE_EXPAND_ATTR = 'data-dn-gift-note-message-expand';
  const MESSAGE_COLLAPSE_ATTR = 'data-dn-gift-note-message-collapse';
  const MESSAGE_COLLAPSED_HEIGHT_ATTR = 'data-dn-collapsed-height';
  const INJECT_DELAY_MS = 1500;
  const INJECT_CHANGE_DEBOUNCE_MS = 500;
  const TEST_ROW_LOADING_CLASS = 'is-loading';
  const TEST_LABEL_DEFAULT_ATTR = 'data-dn-test-label-default';

  let config = null;
  let modal = null;
  let form = null;
  let fromInput = null;
  let toInput = null;
  let messageInput = null;
  let counterEl = null;
  let errorEl = null;
  let saveBtn = null;
  let lastFocusedElement = null;
  let isSubmitting = false;
  let isAddingWrap = false;
  let isRemovingWrap = false;
  let isRemovingNote = false;
  let injectTimer = null;
  let changeInjectTimer = null;
  let cartClickBound = false;

  function readConfig() {
    const el = document.getElementById(CONFIG_EL_ID);
    if (!el) return null;

    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('[DN Gift Purchase] Failed to parse config.', err);
      return null;
    }
  }

  function getNoteConfig() {
    return config?.giftNote || null;
  }

  function getWrapConfig() {
    return config?.giftWrapping || null;
  }

  function isNoteActive() {
    const note = getNoteConfig();
    return Boolean(note?.enabled && note?.variantId);
  }

  function isWrapActive() {
    const wrap = getWrapConfig();
    return Boolean(wrap?.enabled && wrap?.variantId);
  }

  function isCollapsibleSection(section) {
    return section?.hasAttribute(COLLAPSIBLE_ATTR) === true;
  }

  function getCollapsibleHeading() {
    return (config?.heading || 'Make It A Gift').trim();
  }

  function formatTestPrice(formattedPrice) {
    if (!formattedPrice) return '';
    const trimmed = String(formattedPrice).trim();
    if (trimmed.startsWith('+')) return trimmed;
    return `+${trimmed}`;
  }

  function getCartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function normalizeVariantId(value) {
    if (value == null) return null;
    return String(value);
  }

  function getCartItems() {
    const cart = window.Rebuy?.Cart;
    if (!cart) return [];

    const items = cart.items?.() || cart.getItems?.();
    return Array.isArray(items) ? items : [];
  }

  function isCartEmpty() {
    if (getCartItems().length > 0) return false;

    return document.querySelectorAll('#rebuy-cart li.rebuy-cart__flyout-item').length === 0;
  }

  function removeGiftPurchaseSections() {
    document.querySelectorAll(`[${SECTION_ATTR}]`).forEach((section) => section.remove());
  }

  function findCartItemByVariantId(variantId) {
    if (!variantId) return null;
    const targetId = normalizeVariantId(variantId);
    return getCartItems().find((item) => normalizeVariantId(item.variant_id) === targetId) || null;
  }

  function findGiftNoteItem() {
    return findCartItemByVariantId(getNoteConfig()?.variantId);
  }

  function findGiftWrappingItem() {
    return findCartItemByVariantId(getWrapConfig()?.variantId);
  }

  function getItemProperties(item) {
    const raw = item?.properties || item?.line_item?.properties;
    if (!raw) return {};

    if (Array.isArray(raw)) {
      return raw.reduce((acc, entry) => {
        const key = entry?.key ?? entry?.name;
        if (key) acc[key] = entry?.value ?? '';
        return acc;
      }, {});
    }

    return raw;
  }

  function propertiesMatch(existingProps, nextProps) {
    return (
      (existingProps.From || '') === nextProps.From &&
      (existingProps.To || '') === nextProps.To &&
      (existingProps.Message || '') === nextProps.Message
    );
  }

  function isModalOpen() {
    return modal?.getAttribute('aria-hidden') === 'false';
  }

  function isSectionPlaced(subtotalNode) {
    const prev = subtotalNode.previousElementSibling;
    return prev?.hasAttribute(SECTION_ATTR) === true;
  }

  function removeLegacyArtifacts(subtotalNode) {
    document
      .querySelectorAll('#cw-gift-wrap-footer-box, .cw-gift-wrap-footer-box')
      .forEach((el) => el.remove());

    const prev = subtotalNode.previousElementSibling;
    if (prev?.hasAttribute(NOTE_ROW_ATTR) && !prev?.hasAttribute(SECTION_ATTR)) {
      prev.remove();
      return;
    }

    if (prev?.hasAttribute(NOTE_TRIGGER_ATTR) && !prev?.hasAttribute(NOTE_ROW_ATTR)) {
      prev.remove();
    }
  }

  function createPurchaseRow(rowConfig, { rowAttr, triggerAttr, actionLabel }) {
    const row = document.createElement('div');
    row.className = 'dn-gift-purchase__row';
    row.setAttribute(rowAttr, 'true');

    if (rowConfig.thumbImageUrl) {
      const thumb = document.createElement('img');
      thumb.className = 'dn-gift-purchase__row-thumb';
      thumb.src = rowConfig.thumbImageUrl;
      thumb.alt = rowConfig.rowLabel || '';
      thumb.width = rowConfig.thumbWidth || 48;
      thumb.height = rowConfig.thumbHeight || 48;
      thumb.loading = 'lazy';
      row.appendChild(thumb);
    }

    const copy = document.createElement('p');
    copy.className = 'dn-gift-purchase__row-copy';

    const label = document.createElement('span');
    label.className = 'dn-gift-purchase__row-label';
    label.textContent = rowConfig.rowLabel || '';
    copy.appendChild(label);

    if (rowConfig.formattedPrice) {
      copy.appendChild(document.createTextNode(' '));
      const price = document.createElement('span');
      price.className = 'dn-gift-purchase__row-price';
      price.textContent = rowConfig.formattedPrice;
      copy.appendChild(price);
    }

    row.appendChild(copy);

    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'dn-gift-purchase__row-action rebuy-button';
    action.setAttribute(triggerAttr, 'true');
    action.textContent = actionLabel || rowConfig.buttonLabelAdd || 'Add';
    action.setAttribute('aria-label', action.textContent);
    row.appendChild(action);

    return row;
  }

  function createTestOptionRow(rowConfig, { rowAttr, isInCart }) {
    const row = document.createElement('label');
    row.className = 'dn-gift-purchase__test-row';
    row.setAttribute(rowAttr, 'true');

    const control = document.createElement('span');
    control.className = 'dn-gift-purchase__test-control';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'dn-gift-purchase__test-checkbox';
    checkbox.checked = Boolean(isInCart);
    checkbox.tabIndex = -1;
    checkbox.setAttribute('aria-hidden', 'true');
    control.appendChild(checkbox);

    const spinner = document.createElement('span');
    spinner.className = 'dn-gift-purchase__test-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    control.appendChild(spinner);

    row.appendChild(control);

    const label = document.createElement('span');
    label.className = 'dn-gift-purchase__test-label';
    label.textContent = rowConfig.rowLabel || '';
    row.appendChild(label);

    const price = document.createElement('span');
    price.className = 'dn-gift-purchase__test-price';
    price.textContent = formatTestPrice(rowConfig.formattedPrice);
    row.appendChild(price);

    return row;
  }

  function isTestRowLoading(row) {
    return row?.classList.contains(TEST_ROW_LOADING_CLASS) === true;
  }

  function setTestRowPending(row, pendingLabel) {
    const labelEl = row?.querySelector('.dn-gift-purchase__test-label');
    if (!labelEl) return;

    if (!labelEl.hasAttribute(TEST_LABEL_DEFAULT_ATTR)) {
      labelEl.setAttribute(TEST_LABEL_DEFAULT_ATTR, labelEl.textContent);
    }

    row.classList.add(TEST_ROW_LOADING_CLASS);
    row.setAttribute('aria-busy', 'true');
    labelEl.textContent = pendingLabel;
  }

  function clearTestRowPending(row) {
    if (!row) return;

    row.classList.remove(TEST_ROW_LOADING_CLASS);
    row.removeAttribute('aria-busy');

    const labelEl = row.querySelector('.dn-gift-purchase__test-label');
    if (labelEl?.hasAttribute(TEST_LABEL_DEFAULT_ATTR)) {
      labelEl.removeAttribute(TEST_LABEL_DEFAULT_ATTR);
    }

    const isNoteRow = row.hasAttribute(NOTE_ROW_ATTR);
    const rowConfig = isNoteRow ? getNoteConfig() : getWrapConfig();
    const isInCart = isNoteRow
      ? Boolean(findGiftNoteItem())
      : Boolean(findGiftWrappingItem());

    if (rowConfig) {
      syncTestOptionRowContent(row, rowConfig, isInCart);
    }
  }

  async function runTestRowCartAction(row, pendingLabel, actionFn) {
    if (!row || isTestRowLoading(row)) return;

    setTestRowPending(row, pendingLabel);

    try {
      await actionFn();
    } finally {
      clearTestRowPending(row);
    }
  }

  function syncTestOptionRowContent(row, rowConfig, isInCart) {
    if (isTestRowLoading(row)) return;

    const label = row.querySelector('.dn-gift-purchase__test-label');
    if (label) label.textContent = rowConfig.rowLabel || '';

    const price = row.querySelector('.dn-gift-purchase__test-price');
    if (price) price.textContent = formatTestPrice(rowConfig.formattedPrice);

    const checkbox = row.querySelector('.dn-gift-purchase__test-checkbox');
    if (checkbox) checkbox.checked = Boolean(isInCart);
  }

  function syncCollapsibleSummary(section) {
    const summary = section.querySelector('.dn-gift-purchase__summary');
    if (!summary) return;

    const headingText = getCollapsibleHeading();
    const title = summary.querySelector('.dn-gift-purchase__summary-title');
    if (title && title.textContent !== headingText) {
      title.textContent = headingText;
    }
  }

  function syncCollapsibleSectionRows(section) {
    const rowsContainer = section.querySelector('.dn-gift-purchase__rows');
    if (!rowsContainer) return;

    if (isCartEmpty()) {
      section.remove();
      return;
    }

    const noteConfig = getNoteConfig();
    const wrapConfig = getWrapConfig();
    let wrapRow = rowsContainer.querySelector(`[${WRAP_ROW_ATTR}]`);
    let noteRow = rowsContainer.querySelector(`[${NOTE_ROW_ATTR}]`);

    if (isWrapActive()) {
      const wrapInCart = Boolean(findGiftWrappingItem());
      if (!wrapRow) {
        wrapRow = createTestOptionRow(wrapConfig, {
          rowAttr: WRAP_ROW_ATTR,
          isInCart: wrapInCart
        });
        rowsContainer.appendChild(wrapRow);
      } else {
        syncTestOptionRowContent(wrapRow, wrapConfig, wrapInCart);
      }
    } else {
      wrapRow?.remove();
    }

    if (isNoteActive()) {
      const noteInCart = Boolean(findGiftNoteItem());
      if (!noteRow) {
        noteRow = createTestOptionRow(noteConfig, {
          rowAttr: NOTE_ROW_ATTR,
          isInCart: noteInCart
        });
        rowsContainer.appendChild(noteRow);
      } else {
        syncTestOptionRowContent(noteRow, noteConfig, noteInCart);
      }
    } else {
      noteRow?.remove();
    }

    if (!rowsContainer.children.length) {
      section.remove();
    }
  }

  function syncPurchaseRowContent(row, rowConfig) {
    const thumb = row.querySelector('.dn-gift-purchase__row-thumb');
    if (thumb && rowConfig.thumbImageUrl) {
      thumb.src = rowConfig.thumbImageUrl;
      thumb.alt = rowConfig.rowLabel || '';
    }

    const label = row.querySelector('.dn-gift-purchase__row-label');
    if (label) label.textContent = rowConfig.rowLabel || '';

    const price = row.querySelector('.dn-gift-purchase__row-price');
    if (price) price.textContent = rowConfig.formattedPrice || '';
  }

  function syncSectionHeading(section) {
    const headingText = (config?.heading || '').trim();
    let heading = section.querySelector('.dn-gift-purchase__heading');

    if (!headingText) {
      heading?.remove();
      return;
    }

    if (!heading) {
      heading = document.createElement('span');
      heading.className = 'dn-gift-purchase__heading';
      section.insertBefore(heading, section.firstChild);
    }

    if (heading.textContent !== headingText) {
      heading.textContent = headingText;
    }
  }

  function syncSectionRows(section) {
    if (isCollapsibleSection(section)) {
      syncCollapsibleSummary(section);
      syncCollapsibleSectionRows(section);
      return;
    }

    const rowsContainer = section.querySelector('.dn-gift-purchase__rows');
    if (!rowsContainer) return;

    if (isCartEmpty()) {
      section.remove();
      return;
    }

    const noteConfig = getNoteConfig();
    const wrapConfig = getWrapConfig();
    let noteRow = rowsContainer.querySelector(`[${NOTE_ROW_ATTR}]`);
    let wrapRow = rowsContainer.querySelector(`[${WRAP_ROW_ATTR}]`);

    if (isNoteActive() && !findGiftNoteItem()) {
      if (!noteRow) {
        noteRow = createPurchaseRow(noteConfig, {
          rowAttr: NOTE_ROW_ATTR,
          triggerAttr: NOTE_TRIGGER_ATTR,
          actionLabel: noteConfig.buttonLabelAdd
        });
        rowsContainer.appendChild(noteRow);
      } else {
        syncPurchaseRowContent(noteRow, noteConfig);
        const action = noteRow.querySelector(`[${NOTE_TRIGGER_ATTR}]`);
        if (action) {
          action.textContent = noteConfig.buttonLabelAdd;
          action.setAttribute('aria-label', noteConfig.buttonLabelAdd);
        }
      }
    } else {
      noteRow?.remove();
    }

    if (isWrapActive() && !findGiftWrappingItem()) {
      if (!wrapRow) {
        wrapRow = createPurchaseRow(wrapConfig, {
          rowAttr: WRAP_ROW_ATTR,
          triggerAttr: WRAP_TRIGGER_ATTR,
          actionLabel: wrapConfig.buttonLabelAdd
        });
        rowsContainer.appendChild(wrapRow);
      } else {
        syncPurchaseRowContent(wrapRow, wrapConfig);
      }
    } else {
      wrapRow?.remove();
    }

    if (!rowsContainer.children.length) {
      section.remove();
    }
  }

  function createCollapsibleGiftPurchaseSection() {
    const details = document.createElement('details');
    details.className = 'dn-gift-purchase dn-gift-purchase--collapsible';
    details.setAttribute(SECTION_ATTR, 'true');
    details.setAttribute(COLLAPSIBLE_ATTR, 'true');

    const summary = document.createElement('summary');
    summary.className = 'dn-gift-purchase__summary';

    const title = document.createElement('span');
    title.className = 'dn-gift-purchase__summary-title';
    title.textContent = getCollapsibleHeading();
    summary.appendChild(title);

    const toggle = document.createElement('span');
    toggle.className = 'dn-gift-purchase__summary-toggle';
    toggle.setAttribute('aria-hidden', 'true');
    summary.appendChild(toggle);

    const rowsContainer = document.createElement('div');
    rowsContainer.className = 'dn-gift-purchase__rows dn-gift-purchase__rows--test';

    details.appendChild(summary);
    details.appendChild(rowsContainer);
    syncCollapsibleSectionRows(details);

    return details;
  }

  function createGiftPurchaseSection() {
    return createCollapsibleGiftPurchaseSection();
  }

  function sectionHasVisibleRows(section) {
    if (isCollapsibleSection(section)) {
      return Boolean(section.querySelector('.dn-gift-purchase__rows')?.children.length);
    }

    return Boolean(section.querySelector('.dn-gift-purchase__rows')?.children.length);
  }

  function getGiftNoteLineItemElements() {
    const noteConfig = getNoteConfig();
    const handle = noteConfig?.productHandle;
    if (!handle || !findGiftNoteItem()) return [];

    return Array.from(
      document.querySelectorAll('#rebuy-cart li.rebuy-cart__flyout-item')
    ).filter((el) => {
      if (el.classList.contains(`product-${handle}`)) return true;

      const link = el.querySelector('.rebuy-cart__flyout-item-product-title');
      const href = link?.getAttribute('href') || '';
      return href.includes(`/products/${handle}`);
    });
  }

  function removeLegacyPencilEditUI() {
    document.querySelectorAll(`[${NOTE_LINE_ACTIONS_ATTR}]`).forEach((actions) => {
      const removeBtn = actions.querySelector('.rebuy-cart__flyout-item-remove');
      if (removeBtn && actions.parentNode) {
        actions.parentNode.insertBefore(removeBtn, actions);
      }
      actions.remove();
    });
  }

  function removeGiftNoteMessageTruncateUI() {
    document.querySelectorAll(`[${MESSAGE_TRUNCATE_ATTR}]`).forEach((wrapper) => {
      const valueEl = wrapper.closest('.rebuy-cart__flyout-item-property-value');
      const fullText = wrapper.getAttribute(MESSAGE_FULL_ATTR) || '';

      if (valueEl) {
        valueEl.textContent = fullText;
      } else {
        wrapper.remove();
      }
    });
  }

  function removeGiftNoteLineItemEditUI() {
    document.querySelectorAll(`[${NOTE_LINE_EDIT_ATTR}]`).forEach((el) => el.remove());
    removeLegacyPencilEditUI();
  }

  function removeGiftNoteLineItemUI() {
    removeGiftNoteLineItemEditUI();
    removeGiftNoteMessageTruncateUI();
  }

  function truncateMessageText(text) {
    if (text.length <= MESSAGE_DISPLAY_LIMIT) return text;
    return `${text.slice(0, MESSAGE_DISPLAY_LIMIT).trimEnd()}…`;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function shouldDeferInputFocusOnModalOpen() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  function updateMessageToggleButton(toggleBtn, expanded) {
    if (expanded) {
      toggleBtn.removeAttribute(MESSAGE_EXPAND_ATTR);
      toggleBtn.setAttribute(MESSAGE_COLLAPSE_ATTR, 'true');
      toggleBtn.textContent = 'Show less';
      toggleBtn.setAttribute('aria-label', 'Show less');
      return;
    }

    toggleBtn.removeAttribute(MESSAGE_COLLAPSE_ATTR);
    toggleBtn.setAttribute(MESSAGE_EXPAND_ATTR, 'true');
    toggleBtn.textContent = 'Show more';
    toggleBtn.setAttribute('aria-label', 'Show more');
  }

  function readStoredCollapsedHeight(wrapper) {
    const stored = wrapper.getAttribute(MESSAGE_COLLAPSED_HEIGHT_ATTR);
    const height = stored ? Number(stored) : 0;
    return Number.isFinite(height) && height > 0 ? height : null;
  }

  function measureMessageBodyCollapsedHeight(body, textEl, fullText) {
    const previousText = textEl.textContent;
    const previousMaxHeight = body.style.maxHeight;

    textEl.textContent = truncateMessageText(fullText);
    body.style.maxHeight = 'none';
    const height = body.scrollHeight;

    textEl.textContent = previousText;
    body.style.maxHeight = previousMaxHeight;

    return height > 0 ? height : 0;
  }

  function applyMessageBodyCollapsedState(wrapper, body, textEl, fullText) {
    textEl.textContent = truncateMessageText(fullText);
    body.style.maxHeight = 'none';
    const height = body.scrollHeight;

    if (height <= 0) {
      body.style.removeProperty('max-height');
      return 0;
    }

    wrapper.setAttribute(MESSAGE_COLLAPSED_HEIGHT_ATTR, String(height));
    body.style.maxHeight = `${height}px`;
    return height;
  }

  function scheduleMessageBodyCollapsedMeasure(wrapper, body, textEl, fullText) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        applyMessageBodyCollapsedState(wrapper, body, textEl, fullText);
      });
    });
  }

  function getMessageBodyCollapsedHeight(wrapper, body, textEl, fullText) {
    const stored = readStoredCollapsedHeight(wrapper);
    if (stored) return stored;

    const height = measureMessageBodyCollapsedHeight(body, textEl, fullText);
    if (height > 0) {
      wrapper.setAttribute(MESSAGE_COLLAPSED_HEIGHT_ATTR, String(height));
    }
    return height;
  }

  function pinMessageBodyHeight(body) {
    body.style.maxHeight = `${body.scrollHeight}px`;
  }

  function finishMessageBodyTransition(wrapper, body, textEl, fullText, toggleBtn) {
    wrapper.classList.remove('is-transitioning');

    if (wrapper.classList.contains('is-expanded')) {
      body.style.maxHeight = 'none';
    } else {
      applyMessageBodyCollapsedState(wrapper, body, textEl, fullText);
    }

    updateMessageToggleButton(toggleBtn, wrapper.classList.contains('is-expanded'));
  }

  function bindMessageBodyTransitionEnd(wrapper, body, textEl, fullText, toggleBtn) {
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(fallbackTimer);
      body.removeEventListener('transitionend', onEnd);
      finishMessageBodyTransition(wrapper, body, textEl, fullText, toggleBtn);
    };

    const onEnd = (event) => {
      if (event.target !== body || event.propertyName !== 'max-height') return;
      finish();
    };

    const fallbackTimer = setTimeout(finish, 350);

    body.addEventListener('transitionend', onEnd);
  }

  function buildMessageTruncateMarkup(fullText, expanded) {
    const wrapper = document.createElement('span');
    wrapper.className = 'dn-gift-note-message-truncate';
    if (expanded) wrapper.classList.add('is-expanded');
    wrapper.setAttribute(MESSAGE_TRUNCATE_ATTR, 'true');
    wrapper.setAttribute(MESSAGE_FULL_ATTR, fullText);

    const body = document.createElement('span');
    body.className = 'dn-gift-note-message-body';

    const textEl = document.createElement('span');
    textEl.className = 'dn-gift-note-message-text';
    textEl.textContent = expanded ? fullText : truncateMessageText(fullText);
    body.appendChild(textEl);

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'dn-gift-note-message-toggle';
    updateMessageToggleButton(toggle, expanded);

    wrapper.appendChild(body);
    wrapper.appendChild(toggle);

    if (expanded) {
      body.style.maxHeight = 'none';
    }

    return wrapper;
  }

  function truncateGiftNoteMessageDisplay() {
    if (!isNoteActive() || !findGiftNoteItem()) {
      removeGiftNoteMessageTruncateUI();
      return;
    }

    const giftNoteItem = findGiftNoteItem();
    const fullMessage = getItemProperties(giftNoteItem).Message || '';

    getGiftNoteLineItemElements().forEach((lineItem) => {
      const messageRow = findMessagePropertyRow(lineItem);
      if (!messageRow) return;

      const valueEl = messageRow.querySelector('.rebuy-cart__flyout-item-property-value');
      if (!valueEl) return;

      const existingWrapper = valueEl.querySelector(`[${MESSAGE_TRUNCATE_ATTR}]`);

      if (fullMessage.length <= MESSAGE_DISPLAY_LIMIT) {
        if (existingWrapper || valueEl.textContent !== fullMessage) {
          valueEl.textContent = fullMessage;
        }
        return;
      }

      const wasExpanded =
        existingWrapper?.classList.contains('is-expanded') &&
        existingWrapper.getAttribute(MESSAGE_FULL_ATTR) === fullMessage;
      const storedFull = existingWrapper?.getAttribute(MESSAGE_FULL_ATTR);

      if (existingWrapper && storedFull === fullMessage) {
        if (existingWrapper.classList.contains('is-transitioning')) return;
        if (existingWrapper.querySelector('.dn-gift-note-message-body')) return;
      }

      valueEl.textContent = '';
      const wrapper = buildMessageTruncateMarkup(fullMessage, wasExpanded);
      valueEl.appendChild(wrapper);

      if (!wasExpanded) {
        const body = wrapper.querySelector('.dn-gift-note-message-body');
        const textEl = wrapper.querySelector('.dn-gift-note-message-text');
        if (body && textEl) {
          scheduleMessageBodyCollapsedMeasure(wrapper, body, textEl, fullMessage);
        }
      }
    });
  }

  function toggleGiftNoteMessageExpandInstant(toggleBtn, expand) {
    const wrapper = toggleBtn.closest(`[${MESSAGE_TRUNCATE_ATTR}]`);
    if (!wrapper) return;

    const fullText = wrapper.getAttribute(MESSAGE_FULL_ATTR) || '';
    const body = wrapper.querySelector('.dn-gift-note-message-body');
    const textEl = wrapper.querySelector('.dn-gift-note-message-text');
    if (!body || !textEl) return;

    updateMessageToggleButton(toggleBtn, expand);

    if (expand) {
      wrapper.classList.add('is-expanded');
      textEl.textContent = fullText;
      body.style.maxHeight = 'none';
      return;
    }

    wrapper.classList.remove('is-expanded');
    applyMessageBodyCollapsedState(wrapper, body, textEl, fullText);
  }

  function toggleGiftNoteMessageExpand(toggleBtn, expand) {
    const wrapper = toggleBtn.closest(`[${MESSAGE_TRUNCATE_ATTR}]`);
    if (!wrapper || wrapper.classList.contains('is-transitioning')) return;

    const fullText = wrapper.getAttribute(MESSAGE_FULL_ATTR) || '';
    const body = wrapper.querySelector('.dn-gift-note-message-body');
    const textEl = wrapper.querySelector('.dn-gift-note-message-text');
    if (!body || !textEl) return;

    if (prefersReducedMotion()) {
      toggleGiftNoteMessageExpandInstant(toggleBtn, expand);
      return;
    }

    if (!expand && !wrapper.classList.contains('is-expanded')) {
      return;
    }

    let collapseTargetHeight = null;
    if (!expand) {
      collapseTargetHeight = getMessageBodyCollapsedHeight(wrapper, body, textEl, fullText);
      if (!collapseTargetHeight) {
        toggleGiftNoteMessageExpandInstant(toggleBtn, false);
        return;
      }
    }

    wrapper.classList.add('is-transitioning');
    bindMessageBodyTransitionEnd(wrapper, body, textEl, fullText, toggleBtn);

    if (expand) {
      pinMessageBodyHeight(body);
      textEl.textContent = fullText;
      wrapper.classList.add('is-expanded');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          body.style.maxHeight = `${body.scrollHeight}px`;
        });
      });
      return;
    }

    if (body.style.maxHeight === 'none' || !body.style.maxHeight) {
      textEl.textContent = fullText;
    }

    pinMessageBodyHeight(body);
    wrapper.classList.remove('is-expanded');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        body.style.maxHeight = `${collapseTargetHeight}px`;
      });
    });
  }

  function findMessagePropertyRow(lineItem) {
    const propertyRows = lineItem.querySelectorAll('.rebuy-cart__flyout-item-property');

    return (
      Array.from(propertyRows).find((row) => {
        const nameEl = row.querySelector('.rebuy-cart__flyout-item-property-name');
        return nameEl?.textContent.trim().toLowerCase() === 'message';
      }) || null
    );
  }

  function createGiftNoteEditLink() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dn-gift-note-line-item-edit-link';
    btn.setAttribute(NOTE_LINE_EDIT_ATTR, 'true');
    btn.setAttribute('aria-label', EDIT_NOTE_LABEL);
    btn.textContent = EDIT_NOTE_LABEL;
    return btn;
  }

  function injectGiftNoteLineItemEdit() {
    removeLegacyPencilEditUI();

    if (!isNoteActive() || !findGiftNoteItem()) {
      removeGiftNoteLineItemUI();
      return;
    }

    getGiftNoteLineItemElements().forEach((lineItem) => {
      const messageRow = findMessagePropertyRow(lineItem);
      if (!messageRow) return;

      const nextEl = messageRow.nextElementSibling;
      if (nextEl?.hasAttribute(NOTE_LINE_EDIT_ATTR)) return;

      messageRow.insertAdjacentElement('afterend', createGiftNoteEditLink());
    });
  }

  function runGiftPurchaseUpdates() {
    injectGiftPurchaseSection();
    truncateGiftNoteMessageDisplay();
    injectGiftNoteLineItemEdit();
  }

  function isSectionMissing() {
    const subtotalNodes = document.querySelectorAll(
      '#rebuy-cart [data-rebuy-component="cart-subtotal"]'
    );

    return Array.from(subtotalNodes).some((node) => !isSectionPlaced(node));
  }

  function injectGiftPurchaseSection() {
    if (isModalOpen() || isAddingWrap) return;
    if (!isNoteActive() && !isWrapActive()) return;

    if (isCartEmpty()) {
      removeGiftPurchaseSections();
      return;
    }

    const subtotalNodes = document.querySelectorAll(
      '#rebuy-cart [data-rebuy-component="cart-subtotal"]'
    );

    subtotalNodes.forEach((subtotalNode) => {
      const parent = subtotalNode.parentElement;
      if (!parent) return;

      removeLegacyArtifacts(subtotalNode);

      if (isSectionPlaced(subtotalNode)) {
        const section = subtotalNode.previousElementSibling;
        syncCollapsibleSummary(section);
        syncCollapsibleSectionRows(section);
        return;
      }

      const section = createGiftPurchaseSection();
      if (!sectionHasVisibleRows(section)) return;

      parent.insertBefore(section, subtotalNode);
    });
  }

  function scheduleInject(delay = INJECT_DELAY_MS) {
    if (injectTimer) clearTimeout(injectTimer);
    injectTimer = setTimeout(runGiftPurchaseUpdates, delay);
  }

  function scheduleInjectOnChange() {
    if (changeInjectTimer) clearTimeout(changeInjectTimer);
    changeInjectTimer = setTimeout(() => {
      if (isSectionMissing()) {
        runGiftPurchaseUpdates();
      } else {
        document.querySelectorAll(`[${SECTION_ATTR}]`).forEach((section) => {
          syncCollapsibleSummary(section);
          syncCollapsibleSectionRows(section);
        });

        if (isSectionMissing()) {
          injectGiftPurchaseSection();
        }

        truncateGiftNoteMessageDisplay();
        injectGiftNoteLineItemEdit();
      }
    }, INJECT_CHANGE_DEBOUNCE_MS);
  }

  function handleCartClick(event) {
    if (!event.target.closest('#rebuy-cart')) return;

    const expandBtn = event.target.closest(`[${MESSAGE_EXPAND_ATTR}]`);
    if (expandBtn) {
      event.preventDefault();
      toggleGiftNoteMessageExpand(expandBtn, true);
      return;
    }

    const collapseBtn = event.target.closest(`[${MESSAGE_COLLAPSE_ATTR}]`);
    if (collapseBtn) {
      event.preventDefault();
      toggleGiftNoteMessageExpand(collapseBtn, false);
      return;
    }

    if (
      event.target.closest(`[${NOTE_TRIGGER_ATTR}]`) ||
      event.target.closest(`[${NOTE_LINE_EDIT_ATTR}]`)
    ) {
      openModal();
      return;
    }

    const testNoteRow = event.target.closest(
      `.dn-gift-purchase__test-row[${NOTE_ROW_ATTR}]`
    );
    if (testNoteRow) {
      event.preventDefault();
      if (isTestRowLoading(testNoteRow)) return;

      if (findGiftNoteItem()) {
        void runTestRowCartAction(testNoteRow, 'Removing', removeGiftNote);
      } else {
        openModal();
      }
      return;
    }

    const testWrapRow = event.target.closest(
      `.dn-gift-purchase__test-row[${WRAP_ROW_ATTR}]`
    );
    if (testWrapRow) {
      event.preventDefault();
      if (isTestRowLoading(testWrapRow)) return;

      if (findGiftWrappingItem()) {
        void runTestRowCartAction(testWrapRow, 'Removing', removeGiftWrapping);
      } else {
        void runTestRowCartAction(testWrapRow, 'Adding', addGiftWrapping);
      }
      return;
    }

    if (event.target.closest(`[${WRAP_TRIGGER_ATTR}]`)) {
      addGiftWrapping();
    }
  }

  function bindCartClickDelegation() {
    if (cartClickBound) return;
    document.addEventListener('click', handleCartClick);
    cartClickBound = true;
  }

  function setError(message) {
    if (!errorEl) return;

    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  function updateCounter() {
    if (!counterEl || !messageInput) return;
    counterEl.textContent = String(messageInput.value.length);
  }

  function populateFormFromCart() {
    const item = findGiftNoteItem();
    const props = getItemProperties(item);

    if (fromInput) fromInput.value = props.From || '';
    if (toInput) toInput.value = props.To || '';
    if (messageInput) messageInput.value = props.Message || '';
    updateCounter();
    setError('');
  }

  function getFocusableElements() {
    if (!modal) return [];

    return Array.from(
      modal.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null || el === saveBtn);
  }

  function trapFocus(event) {
    if (!modal || modal.getAttribute('aria-hidden') === 'true') return;
    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openModal() {
    if (!modal) return;

    populateFormFromCart();
    lastFocusedElement = document.activeElement;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('dn-gift-note-modal-open');

    let focusTarget;
    if (shouldDeferInputFocusOnModalOpen()) {
      focusTarget = modal.querySelector('[data-dn-gift-note-close]');
    } else {
      focusTarget = fromInput || modal.querySelector('[data-dn-gift-note-close]');
    }

    if (focusTarget) focusTarget.focus();
  }

  function closeModal() {
    if (!modal) return;

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('dn-gift-note-modal-open');
    setError('');

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  function validateForm() {
    const noteConfig = getNoteConfig();
    const from = fromInput?.value.trim() || '';
    const to = toInput?.value.trim() || '';
    const message = messageInput?.value.trim() || '';
    const maxLength = noteConfig?.maxMessageLength || 400;

    if (!from || !to || !message) {
      setError('Please complete From, To, and Message before saving.');
      return null;
    }

    if (message.length > maxLength) {
      setError(`Message must be ${maxLength} characters or fewer.`);
      return null;
    }

    setError('');
    return { From: from, To: to, Message: message, _gift_note: 'true' };
  }

  function waitForRebuyCartAction(action, isDone, errorMessage) {
    return new Promise((resolve, reject) => {
      let settled = false;

      const finish = (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        document.removeEventListener('rebuy:cart.change', onChange);
        if (err) reject(err);
        else resolve();
      };

      const onChange = () => {
        if (isDone()) finish();
      };

      const timer = setTimeout(() => {
        finish(new Error(errorMessage || 'Cart update timed out. Please try again.'));
      }, 10000);

      document.addEventListener('rebuy:cart.change', onChange);

      try {
        action({
          success: () => finish(),
          error: (err) => finish(err || new Error(errorMessage || 'Cart update failed.'))
        });
      } catch (err) {
        finish(err);
      }
    });
  }

  function refreshRebuyCart() {
    return new Promise((resolve) => {
      const cart = window.Rebuy?.Cart;
      if (!cart?.fetchShopifyCart) {
        resolve();
        return;
      }

      cart.fetchShopifyCart(() => resolve());
    });
  }

  async function addViaRebuy(variantId, properties) {
    const cart = window.Rebuy?.Cart;
    if (!cart?.addItem) {
      throw new Error('Rebuy cart is not ready.');
    }

    const payload = { id: variantId, quantity: 1 };
    if (properties) payload.properties = properties;

    await waitForRebuyCartAction(
      (callbacks) => {
        cart.addItem(payload, callbacks);
      },
      () => Boolean(findCartItemByVariantId(variantId)),
      'Failed to add item to cart.'
    );
  }

  async function updateViaRebuy(item, properties) {
    const cart = window.Rebuy?.Cart;
    if (!cart?.changeItem) {
      throw new Error('Rebuy cart is not ready.');
    }

    await waitForRebuyCartAction(
      (callbacks) => {
        cart.changeItem(
          {
            id: item.key,
            quantity: 1,
            properties
          },
          callbacks
        );
      },
      () => true,
      'Failed to update gift note.'
    );
  }

  async function removeViaRebuy(item) {
    const cart = window.Rebuy?.Cart;
    if (!cart?.changeItem) {
      throw new Error('Rebuy cart is not ready.');
    }

    const itemKey = item.key;

    await waitForRebuyCartAction(
      (callbacks) => {
        cart.changeItem({ id: itemKey, quantity: 0 }, callbacks);
      },
      () => !getCartItems().some((cartItem) => cartItem.key === itemKey),
      'Failed to remove item from cart.'
    );
  }

  async function removeViaShopify(item) {
    const res = await fetch(`${getCartRoot()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: item.key,
        quantity: 0
      })
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody?.description || 'Failed to remove item from cart.');
    }

    await refreshRebuyCart();
  }

  async function removeCartLineItem(item) {
    if (!item?.key) return;

    if (window.Rebuy?.Cart?.changeItem) {
      await removeViaRebuy(item);
    } else {
      await removeViaShopify(item);
    }
  }

  async function addViaShopify(variantId, properties) {
    const item = { id: variantId, quantity: 1 };
    if (properties) item.properties = properties;

    const res = await fetch(`${getCartRoot()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] })
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody?.description || 'Failed to add item to cart.');
    }

    await refreshRebuyCart();
  }

  async function updateViaShopify(item, properties) {
    const res = await fetch(`${getCartRoot()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: item.key,
        quantity: 1,
        properties
      })
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody?.description || 'Failed to update gift note.');
    }

    await refreshRebuyCart();
  }

  async function addGiftWrapping() {
    const wrapConfig = getWrapConfig();
    if (!wrapConfig?.variantId || findGiftWrappingItem() || isAddingWrap) return false;

    isAddingWrap = true;

    try {
      if (window.Rebuy?.Cart?.addItem) {
        await addViaRebuy(wrapConfig.variantId);
      } else {
        await addViaShopify(wrapConfig.variantId);
      }

      scheduleInject(INJECT_DELAY_MS);
      return true;
    } catch (err) {
      console.error('[DN Gift Purchase] Add gift wrapping failed.', err);
      return false;
    } finally {
      isAddingWrap = false;
    }
  }

  async function removeGiftWrapping() {
    const item = findGiftWrappingItem();
    if (!item || isRemovingWrap) return false;

    isRemovingWrap = true;

    try {
      await removeCartLineItem(item);
      scheduleInject(INJECT_DELAY_MS);
      return true;
    } catch (err) {
      console.error('[DN Gift Purchase] Remove gift wrapping failed.', err);
      return false;
    } finally {
      isRemovingWrap = false;
    }
  }

  async function removeGiftNote() {
    const item = findGiftNoteItem();
    if (!item || isRemovingNote) return false;

    isRemovingNote = true;

    try {
      await removeCartLineItem(item);
      scheduleInject(INJECT_DELAY_MS);
      return true;
    } catch (err) {
      console.error('[DN Gift Purchase] Remove gift note failed.', err);
      return false;
    } finally {
      isRemovingNote = false;
    }
  }

  async function saveGiftNote(event) {
    event.preventDefault();
    if (isSubmitting) return;

    const noteConfig = getNoteConfig();
    const properties = validateForm();
    if (!properties) return;

    const existingItem = findGiftNoteItem();

    if (existingItem && propertiesMatch(getItemProperties(existingItem), properties)) {
      closeModal();
      return;
    }

    isSubmitting = true;
    if (saveBtn) saveBtn.disabled = true;

    try {
      if (existingItem) {
        if (window.Rebuy?.Cart?.changeItem) {
          await updateViaRebuy(existingItem, properties);
        } else {
          await updateViaShopify(existingItem, properties);
        }
      } else if (window.Rebuy?.Cart?.addItem) {
        await addViaRebuy(noteConfig.variantId, properties);
      } else {
        await addViaShopify(noteConfig.variantId, properties);
      }

      closeModal();
      scheduleInject(INJECT_DELAY_MS);
    } catch (err) {
      console.error('[DN Gift Purchase] Save gift note failed.', err);
      setError(err?.message || 'Could not save your gift note. Please try again.');
    } finally {
      isSubmitting = false;
      if (saveBtn) saveBtn.disabled = false;
    }
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && modal?.getAttribute('aria-hidden') === 'false') {
      closeModal();
    }
    trapFocus(event);
  }

  function bindModalEvents() {
    if (!modal || !form) return;

    form.addEventListener('submit', saveGiftNote);
    messageInput?.addEventListener('input', updateCounter);

    modal.querySelectorAll('[data-dn-gift-note-close]').forEach((el) => {
      el.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', handleKeydown);
  }

  function bindCartEvents() {
    const lifecycleEvents = [
      'rebuy:smartcart.ready',
      'rebuy:smartcart.show',
      'rebuy:cart.ready',
      'rebuy:cart.add'
    ];

    lifecycleEvents.forEach((eventName) => {
      document.addEventListener(eventName, () => scheduleInject(INJECT_DELAY_MS));
    });

    document.addEventListener('rebuy:cart.change', scheduleInjectOnChange);

    document.addEventListener('rebuy:smartcart.line-item-removed', (event) => {
      const item = event.detail?.item;
      if (!item) return;

      const variantId = normalizeVariantId(item.variant_id);
      const noteId = normalizeVariantId(getNoteConfig()?.variantId);
      const wrapId = normalizeVariantId(getWrapConfig()?.variantId);

      if (variantId === noteId || variantId === wrapId) {
        scheduleInject(INJECT_DELAY_MS);
      }
    });
  }

  function onCartRootReady() {
    scheduleInject(INJECT_DELAY_MS);
  }

  function waitForCartRoot() {
    if (document.querySelector('#rebuy-cart')) {
      onCartRootReady();
      return;
    }

    const rootObserver = new MutationObserver(() => {
      if (document.querySelector('#rebuy-cart')) {
        rootObserver.disconnect();
        onCartRootReady();
      }
    });

    rootObserver.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    config = readConfig();
    if (!isNoteActive() && !isWrapActive()) return;

    if (isNoteActive()) {
      modal = document.getElementById(MODAL_ID);
      if (modal) {
        form = document.getElementById('DnGiftNoteForm');
        fromInput = document.getElementById('DnGiftNoteFrom');
        toInput = document.getElementById('DnGiftNoteTo');
        messageInput = document.getElementById('DnGiftNoteMessage');
        counterEl = modal.querySelector('[data-dn-gift-note-count]');
        errorEl = modal.querySelector('[data-dn-gift-note-error]');
        saveBtn = modal.querySelector('[data-dn-gift-note-save]');

        bindModalEvents();
      } else {
        console.error('[DN Gift Purchase] Gift note enabled but modal markup is missing.');
      }
    }

    bindCartEvents();
    bindCartClickDelegation();
    waitForCartRoot();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
